// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import { useAnalytics } from '../../controls/analytics';
import type { AggregatesListingsAnalytics_aggregatesListings$key } from './__generated__/AggregatesListingsAnalytics_aggregatesListings.graphql';

type Props = {|
  aggregatesListings: ?AggregatesListingsAnalytics_aggregatesListings$key,
|};

export const AggregatesListingsAnalytics: React.AbstractComponent<
  Props,
  mixed,
> = React.memo(props => {
  /* eslint-disable relay/unused-fields */
  const aggregatesListings = useFragment(
    graphql`
      fragment AggregatesListingsAnalytics_aggregatesListings on AggregatesListingConnectionConnection {
        filters {
          offerType_eq
          salePrice_lte
          salePrice_gte
          createdAt_gte
          grossRentMonthly_lte
          grossRentMonthly_gte
          grossRentM2yearly_lte
          grossRentM2yearly_gte
          grossRentRoomMonthly_lte
          grossRentRoomMonthly_gte
          salePricePerLivingSurface_lte
          salePricePerLivingSurface_gte
          salePricePerLandSurface_lte
          salePricePerLandSurface_gte
          numberOfRooms_lte
          numberOfRooms_gte
          livingSurface_lte
          livingSurface_gte
          landSurface_lte
          landSurface_gte
          compositePropertyType_eq
          portal_in
          agencyReference_contains
          isNewDevelopment_eq
          hasBalcony_eq
          hasGarden_eq
          hasParking_eq
          hasElevator_eq
          hasView_eq
          hasFireplace_eq
          hasGarage_eq
          isChildFriendly_eq
          isWheelchairAccessible_eq
          isMinergieCertified_eq
          isFurnished_eq
          createdAt_gte
          bookOnline_eq
          isFsbo_eq
          places {
            type
            label
            municipality: parentPlace(type: municipality) {
              label
            }
            state: parentPlace(type: state) {
              label
            }
          }
        }
        totalCount
        edges {
          node {
            id
            encodedId
          }
        }
      }
    `,
    props.aggregatesListings,
  );
  /* eslint-enable relay/unused-fields */

  const { track } = useAnalytics();

  React.useEffect(() => {
    if (aggregatesListings != null) {
      const { filters, edges } = aggregatesListings;
      const listingsIds = (edges || []).map(edge => edge?.node.id);
      const listingsEncodedIds = (edges || []).map(
        edge => edge?.node.encodedId,
      );
      const { places, ...otherFilters } = filters;

      track('RA.Web.Listings.Searched', {
        ...otherFilters,
        places,
        listingsIds,
        listingsEncodedIds,
        nonInteraction: 1,
      });

      window.dataLayer.push({
        event: 'select_filter',
        offer_typ_eq: filters.offerType_eq,
        property_type_eq: filters.compositePropertyType_eq,
        number_of_rooms_gte: filters.numberOfRooms_gte,
        number_of_rooms_lte: filters.numberOfRooms_lte,
        living_surface_gte: filters.livingSurface_gte,
        living_surface_lte: filters.livingSurface_lte,
        land_surface_gte: filters.landSurface_gte,
        land_surface_lte: filters.landSurface_lte,
        sale_price_gte: filters.salePrice_gte,
        sale_price_lte: filters.salePrice_lte,
        gross_rent_monthly_gte: filters.grossRentMonthly_gte,
        gross_rent_monthly_lte: filters.grossRentMonthly_lte,
        gross_rent_m2yearly_gte: filters.grossRentM2yearly_gte,
        gross_rent_m2yearly_lte: filters.grossRentM2yearly_lte,
        gross_rent_room_monthly_gte: filters.grossRentRoomMonthly_gte,
        gross_rent_room_monthly_lte: filters.grossRentRoomMonthly_lte,
        sale_price_per_living_surface_gte:
          filters.salePricePerLivingSurface_gte,
        sale_price_per_living_surface_lte:
          filters.salePricePerLivingSurface_lte,
        sale_price_per_land_surface_gte: filters.salePricePerLandSurface_gte,
        sale_price_per_land_surface_lte: filters.salePricePerLandSurface_lte,
        portal_in: filters.portal_in,
        agency_reference_contains: filters.agencyReference_contains,
        has_garden_eq: filters.hasGarden_eq,
        has_garage_eq: filters.hasGarage_eq,
        has_balcony_eq: filters.hasBalcony_eq,
        has_parking_eq: filters.hasParking_eq,
        has_elevator_eq: filters.hasElevator_eq,
        has_view_eq: filters.hasView_eq,
        is_furnished_eq: filters.isFurnished_eq,
        is_fsbo_eq: filters.isFsbo_eq,
        created_at_gte: filters.createdAt_gte,
        book_online_eq: filters.bookOnline_eq,
        has_fireplace_eq: filters.hasFireplace_eq,
        is_child_friendly_eq: filters.isChildFriendly_eq,
        is_new_development_eq: filters.isNewDevelopment_eq,
        is_minergie_certified_eq: filters.isMinergieCertified_eq,
        is_wheelchair_accessible_eq: filters.isWheelchairAccessible_eq,
        places,
      });
    }
  });

  return null;
});
